import {
  ScanLine,
  CreditCard,
  MonitorPlay,
  Ticket,
  Network,
  ArrowUpRight,
} from "lucide-react";

const SOLUCIONES = [
  {
    id: "autoservicio",
    num: "01",
    name: "Autoservicio",
    tagline: "Kioscos & Self-Checkout",
    description:
      "Kioscos inteligentes de autoatención que aceleran la operación, reducen filas y entregan una experiencia moderna al cliente final.",
    bullets: ["Pago integrado", "Pantalla táctil", "Marca personalizable"],
    Icon: ScanLine,
    featured: true,
  },
  {
    id: "pos",
    num: "02",
    name: "POS",
    tagline: "Puntos de Venta",
    description:
      "Terminales POS robustos para retail, supermercado y hospitality. Hardware certificado e integración con tu software.",
    bullets: ["Multi-formato", "Alta disponibilidad"],
    Icon: CreditCard,
  },
  {
    id: "carteleria",
    num: "03",
    name: "Cartelería Digital",
    tagline: "Digital Signage",
    description:
      "Pantallas y reproductores con gestión remota de contenido. Tu mensaje, en cualquier punto, controlado desde un solo lugar.",
    bullets: ["CMS centralizado", "Multi-sucursal"],
    Icon: MonitorPlay,
  },
  {
    id: "turnomaticos",
    num: "04",
    name: "Turnomáticos",
    tagline: "Gestión de Turnos",
    description:
      "Sistemas de turno con dispensadores, pantallas y métricas en tiempo real para ordenar el flujo de atención de tus sucursales.",
    bullets: ["KPIs en vivo", "Reportería"],
    Icon: Ticket,
  },
  {
    id: "redes",
    num: "05",
    name: "Redes & Fibra Óptica",
    tagline: "Cableado estructurado · Certificación",
    description:
      "Instalación de puntos de red y fibra óptica con calificación y certificación. Entregamos reporte certificado por punto, realizado con equipos calibrados.",
    bullets: [
      "Certificación Fluke",
      "Reporte por punto",
      "Calibración vigente",
      "Cat 6 / 6A",
      "Monomodo / Multimodo",
    ],
    Icon: Network,
    featured: true,
    featuredWide: true,
  },
];

export const Soluciones = () => {
  return (
    <section
      id="soluciones"
      data-testid="soluciones-section"
      className="relative bg-white py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle navy accent */}
      <div
        aria-hidden
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0A2A4E]/[0.04] blur-[120px]"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1FA08A]/[0.07] blur-[120px]"
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 md:mb-20">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5 text-[11px] uppercase tracking-[0.32em] font-mono">
              <span className="text-[#1FA08A] font-semibold">/ 02</span>
              <span className="h-px w-10 bg-slate-300" />
              <span className="text-slate-500 font-semibold">
                Portafolio · Soluciones
              </span>
            </div>
            <h2
              data-testid="soluciones-heading"
              className="font-heading text-[#0A2A4E] text-4xl sm:text-5xl md:text-[56px] lg:text-[64px] leading-[1.02] tracking-tighter font-bold"
            >
              Soluciones que{" "}
              <span className="relative inline-block">
                <span className="text-[#1FA08A]">mueven</span>
                <svg
                  aria-hidden
                  viewBox="0 0 260 14"
                  className="absolute -bottom-1 left-0 w-full h-3 text-[#1FA08A]/40"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 10 C 60 2, 180 16, 258 6"
                    stroke="currentColor"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                </svg>
              </span>{" "}
              negocios.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              data-testid="soluciones-subtitle"
              className="text-slate-600 text-base md:text-lg leading-relaxed max-w-md lg:ml-auto"
            >
              Cinco líneas integradas para retail, supermercado, farmacia y
              servicios. Hardware, software, instalación y soporte —{" "}
              <span className="text-[#0A2A4E] font-semibold">
                de extremo a extremo.
              </span>
            </p>
          </div>
        </div>

        {/* Bento grid: 1 featured + 4 standard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {SOLUCIONES.map((s) => (
            <SolucionCard key={s.id} solucion={s} />
          ))}
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-slate-200 pt-10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-[#1FA08A] font-mono font-semibold mb-2">
              ¿No encuentras lo que buscas?
            </p>
            <p className="text-[#0A2A4E] text-xl md:text-2xl font-heading font-semibold tracking-tight">
              Diseñamos soluciones a medida para tu operación.
            </p>
          </div>
          <a
            href="#contacto"
            data-testid="soluciones-cta"
            className="cta-cotizar group inline-flex items-center gap-2.5 bg-[#0A2A4E] hover:bg-[#153d6e] text-white px-7 py-3.5 rounded-sm font-semibold text-[14px] tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-md hover:shadow-xl whitespace-nowrap"
          >
            Hablar con un experto
            <span aria-hidden className="cta-dot" />
          </a>
        </div>
      </div>
    </section>
  );
};

const SolucionCard = ({ solucion }) => {
  const { id, num, name, tagline, description, bullets, Icon, featured, featuredWide } =
    solucion;

  return (
    <article
      data-testid={`solucion-card-${id}`}
      className={`group solucion-card relative bg-white border border-slate-200 rounded-md p-7 md:p-8 overflow-hidden transition-all duration-500 hover:border-[#1FA08A]/60 hover:shadow-[0_24px_60px_-30px_rgba(10,42,78,0.35)] hover:-translate-y-1 ${
        featured
          ? "bg-gradient-to-br from-[#0A2A4E] to-[#05162a] border-[#0A2A4E] text-white"
          : ""
      } ${featuredWide ? "md:col-span-2 lg:col-span-3" : ""} ${
        featured && !featuredWide ? "md:col-span-2 lg:col-span-2 lg:row-span-1" : ""
      }`}
    >
      {/* Background pixel pattern on hover */}
      <div
        aria-hidden
        className="absolute -top-4 -right-4 grid grid-cols-3 gap-1.5 opacity-25 transition-opacity duration-500 group-hover:opacity-80"
      >
        {[1, 1, 0, 1, 1, 1, 0, 1, 1].map((v, i) => (
          <span
            key={i}
            className={`w-2 h-2 ${
              v
                ? featured
                  ? "bg-[#2bc9ae]"
                  : "bg-[#1FA08A]"
                : "bg-transparent"
            }`}
          />
        ))}
      </div>

      {/* Number marker */}
      <div className="flex items-center justify-between mb-6">
        <span
          className={`text-[10.5px] font-mono uppercase tracking-[0.28em] font-semibold ${
            featured ? "text-[#2bc9ae]" : "text-[#1FA08A]"
          }`}
        >
          / {num}
        </span>
        <span
          className={`text-[10.5px] font-mono uppercase tracking-[0.22em] ${
            featured ? "text-white/55" : "text-slate-400"
          }`}
        >
          {tagline}
        </span>
      </div>

      {/* Icon */}
      <div
        className={`inline-flex items-center justify-center w-14 h-14 rounded-md mb-6 transition-all duration-500 ${
          featured
            ? "bg-[#1FA08A] text-white group-hover:bg-[#2bc9ae]"
            : "bg-[#0A2A4E]/[0.04] text-[#0A2A4E] group-hover:bg-[#1FA08A] group-hover:text-white"
        }`}
      >
        <Icon className="w-7 h-7" strokeWidth={1.5} />
      </div>

      {/* Title */}
      <h3
        className={`font-heading text-2xl md:text-[28px] font-semibold tracking-tight leading-tight mb-3 ${
          featured ? "text-white" : "text-[#0A2A4E]"
        }`}
      >
        {name}
      </h3>

      {/* Description */}
      <p
        className={`text-[14.5px] leading-relaxed mb-6 ${
          featured ? "text-slate-200/85 max-w-md" : "text-slate-600"
        }`}
      >
        {description}
      </p>

      {/* Bullets */}
      <ul className="flex flex-wrap gap-2 mb-6">
        {bullets.map((b) => (
          <li
            key={b}
            className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold px-2.5 py-1 rounded-sm ${
              featured
                ? "bg-white/10 text-white/90 border border-white/15"
                : "bg-slate-50 text-slate-700 border border-slate-200"
            }`}
          >
            <span
              className={`w-1 h-1 rounded-full ${
                featured ? "bg-[#2bc9ae]" : "bg-[#1FA08A]"
              }`}
            />
            {b}
          </li>
        ))}
      </ul>

      {/* CTA link */}
      <a
        href="#contacto"
        className={`inline-flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-[0.16em] transition-all ${
          featured
            ? "text-[#2bc9ae] hover:text-white"
            : "text-[#0A2A4E] hover:text-[#1FA08A]"
        }`}
      >
        Conocer más
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>

      {/* Bottom accent line */}
      <span
        aria-hidden
        className={`absolute left-0 bottom-0 h-[2px] w-0 group-hover:w-full transition-all duration-700 ${
          featured ? "bg-[#2bc9ae]" : "bg-[#1FA08A]"
        }`}
      />
    </article>
  );
};

export default Soluciones;
